import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { getSettings, updateSettings, listUsers, updateUserRole, listClients, adminCreateUser, setUserActive } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sparkles, Palette, Users, Building2, UserPlus, Power } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshSettings } = useAuth();
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  const load = async () => {
    const [set, us, cs] = await Promise.all([getSettings(), listUsers(showInactiveUsers), listClients()]);
    setS(set);
    setUsers(us);
    setClients(cs);
  };

  useEffect(() => {
    load();
  }, [showInactiveUsers]);

  const save = async (patch) => {
    const next = await updateSettings(patch);
    setS(next);
    await refreshSettings();
    toast.success("Settings updated");
  };

  const changeRole = async (uid, role) => {
    await updateUserRole(uid, role, null);
    toast.success("Role updated");
    load();
  };

  const changeClient = async (uid, client_id, currentRole) => {
    await updateUserRole(uid, currentRole, client_id);
    toast.success("Client assigned");
    load();
  };

  const toggleUserActive = async (u) => {
    const next = u.active === false ? true : false;
    if (!next && !window.confirm(`Deactivate user "${u.name}" (${u.email})?\n\nThey will be signed out and unable to log back in until reactivated.`)) return;
    try {
      await setUserActive(u.user_id, next);
      toast.success(next ? "User activated" : "User deactivated");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    }
  };

  if (!s) return <Layout title="Settings"><div /></Layout>;

  return (
    <Layout title="Settings" breadcrumbs={["Admin", "Settings"]}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Branding */}
        <div className="engineered-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-orange-500" />
            <h2 className="font-display font-bold text-xl text-zinc-950">Branding</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Company name</Label>
              <Input
                data-testid="settings-company"
                value={s.company_name || ""}
                onChange={(e) => setS({ ...s, company_name: e.target.value })}
                onBlur={(e) => save({ company_name: e.target.value })}
                className="rounded-sm h-11"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Logo URL</Label>
              <Input
                data-testid="settings-logo"
                value={s.logo_url || ""}
                onChange={(e) => setS({ ...s, logo_url: e.target.value })}
                onBlur={(e) => save({ logo_url: e.target.value })}
                placeholder="https://…"
                className="rounded-sm h-11"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Login page hero image URL</Label>
              <Input
                data-testid="settings-login-image"
                value={s.login_image_url || ""}
                onChange={(e) => setS({ ...s, login_image_url: e.target.value })}
                onBlur={(e) => save({ login_image_url: e.target.value })}
                placeholder="https://…"
                className="rounded-sm h-11"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Shown on the right panel of the login page.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
                <Palette className="h-3 w-3" /> Primary color
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  data-testid="settings-color"
                  type="color"
                  value={s.primary_color || "#F97316"}
                  onChange={(e) => setS({ ...s, primary_color: e.target.value })}
                  onBlur={(e) => save({ primary_color: e.target.value })}
                  className="h-11 w-20 rounded-sm p-1"
                />
                <span className="font-mono text-sm">{s.primary_color}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Branding palette can be fully customized for white-labeling.
              </p>
            </div>
          </div>
        </div>

        {/* AI Toggle */}
        <div className="engineered-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-orange-500" />
            <h2 className="font-display font-bold text-xl text-zinc-950">AI Features</h2>
          </div>
          <div className="flex items-start justify-between gap-4 py-2">
            <div>
              <div className="font-bold">Enable AI Assistant</div>
              <p className="text-sm text-zinc-600">
                Powers triage suggestions on new work orders and one-click manager summaries.
                Disable to hide all AI affordances.
              </p>
            </div>
            <Switch
              data-testid="settings-ai-toggle"
              checked={!!s.ai_enabled}
              onCheckedChange={(v) => save({ ai_enabled: v })}
            />
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-200">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">Model</div>
            <div className="font-mono text-sm">Claude Sonnet 4.6 · via Emergent LLM Key</div>
          </div>
        </div>

        {/* Users (admin only) */}
        {user?.role === "admin" && (
          <div className="engineered-card lg:col-span-2">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-500" />
                <h2 className="font-display font-bold text-xl text-zinc-950">Users &amp; Roles</h2>
              </div>
              <NewUserDialog clients={clients} onCreated={load} />
            </div>
            <div className="divide-y divide-zinc-100">
              {users.map((u) => (
                <div key={u.user_id} className={`px-5 py-3 flex items-center gap-4 ${u.active === false ? "opacity-60 bg-zinc-50" : ""}`}>
                  <Avatar className="h-9 w-9 rounded-sm">
                    <AvatarImage src={u.picture} alt={u.name} />
                    <AvatarFallback className="rounded-sm bg-zinc-900 text-white font-bold">
                      {u.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-2">
                      {u.name}
                      {u.active === false && (
                        <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold tracking-wider bg-red-50 border border-red-300 text-red-700 rounded-sm uppercase">
                          Inactive
                        </span>
                      )}
                      {u.user_id === user.user_id && (
                        <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold tracking-wider bg-orange-50 border border-orange-300 text-orange-700 rounded-sm uppercase">
                          You
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <Select
                    value={u.role}
                    onValueChange={(r) => changeRole(u.user_id, r)}
                    disabled={u.user_id === user.user_id || u.active === false}
                  >
                    <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-36 rounded-sm h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                  {u.role === "client" && (
                    <Select
                      value={u.client_id || ""}
                      onValueChange={(v) => changeClient(u.user_id, v, u.role)}
                      disabled={u.active === false}
                    >
                      <SelectTrigger
                        data-testid={`client-select-${u.user_id}`}
                        className="w-44 rounded-sm h-9"
                      >
                        <SelectValue placeholder="Link client…" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {u.user_id !== user.user_id && (
                    <button
                      data-testid={`toggle-user-${u.user_id}`}
                      onClick={() => toggleUserActive(u)}
                      title={u.active === false ? "Reactivate user" : "Deactivate user (safer than deleting)"}
                      className={`h-9 w-9 rounded-sm border flex items-center justify-center transition-colors ${
                        u.active === false
                          ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          : "bg-white border-zinc-300 text-zinc-700 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                      }`}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 text-xs text-zinc-500 flex items-center justify-between">
              <label className="inline-flex items-center gap-2">
                <input
                  data-testid="users-show-inactive"
                  type="checkbox"
                  checked={showInactiveUsers}
                  onChange={(e) => setShowInactiveUsers(e.target.checked)}
                />
                Show inactive users
              </label>
              <span className="font-mono">
                {users.filter((u) => u.active !== false).length} active
                {showInactiveUsers ? ` · ${users.filter((u) => u.active === false).length} inactive` : ""}
              </span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function NewUserDialog({ clients, onCreated }) {
  const [open, setOpen] = useState(false);
  const empty = { email: "", password: "", name: "", role: "client", client_id: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Email, password and name are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
        client_id: form.role === "client" ? (form.client_id || null) : null,
      };
      await adminCreateUser(payload);
      toast.success(`User ${payload.email} created as ${payload.role}`);
      setForm(empty);
      setOpen(false);
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create user failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-user-btn"
          className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold h-9"
        >
          <UserPlus className="h-4 w-4 mr-1" /> Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add User</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Full name *</Label>
            <Input data-testid="newuser-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Email *</Label>
            <Input data-testid="newuser-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-11" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Password * <span className="font-normal text-zinc-500">(min 6 chars)</span></Label>
            <Input data-testid="newuser-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-11" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Role *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="newuser-role" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="client">Client</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "client" && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Link to client (optional)</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="newuser-client" className="rounded-sm h-11"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {(clients || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 mt-1">Scopes the portal to this client&apos;s work orders only.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="newuser-submit" className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold">
            {busy ? "Creating…" : "Create User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
